import { useState } from "react";

import { ReactComponent as CaretLeft } from "assets/icons/caret-left.svg";
import { ReactComponent as CaretRight } from "assets/icons/caret-right.svg";
import { Field } from "components/ui/field";
import { NumberInputField, NumberInputRoot } from "components/ui/number-input";
import { PaginationItems, PaginationPageText } from "components/ui/pagination";
import { handleGetMaxPage } from "utils";

import { Button } from "ui/button";

import { NumberInputStyle } from "./style";
import { IPaginationMainProps } from "./types";

const ZERO = 0;
const ONE = 1;

export const PaginationMain = (props: IPaginationMainProps) => {
  const { paginationParams, updatePaginationParams, count } = props;
  const { page, limit } = paginationParams;
  const [pageValue, setPageValue] = useState<string>(ONE.toString());
  const maxPage = handleGetMaxPage(count, limit);

  const allowPageJump =
    +pageValue > maxPage || page === +pageValue ? false : true;

  const handleDecrement = () => {
    if (page <= ONE) return;
    const newPage = page - ONE;
    updatePaginationParams({
      page: newPage,
      limit,
    });
  };

  const handleIncrement = () => {
    if (page >= maxPage) return;
    const newPage = page + ONE;
    updatePaginationParams({
      page: newPage,
      limit,
    });
  };

  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    // Allow empty input, but restrict input to positive integers only
    if (value === "" || (/^\d+$/.test(value) && Number(value) >= ZERO)) {
      setPageValue(value);
    }
  };

  const handleSubmit = () => {
    const pageValueNumber = Number(pageValue);
    if (!allowPageJump) return;
    // Reset to a valid default if the input is cleared or empty
    if (pageValueNumber < ONE) {
      setPageValue(ONE.toString());
      updatePaginationParams({ page: ONE, limit });
      return;
    }

    updatePaginationParams({ page: pageValueNumber, limit });
  };

  return (
    <>
      <Button
        border="1px solid #E2E8F0"
        borderRadius="6px"
        icon={<CaretLeft />}
        variant="outline"
        aria-label="Previous Page"
        textColor="#807F94"
        fontWeight={500}
        marginRight="12px"
        onClick={handleDecrement}
        disabled={page <= ONE}
      >
        Prev
      </Button>
      {<PaginationItems /> || <PaginationPageText />}
      <Button
        border="1px solid #E2E8F0"
        borderRadius="6px"
        icon={<CaretRight />}
        isRightIcon
        variant="outline"
        aria-label="Next Page"
        textColor="#807F94"
        fontWeight={500}
        onClick={handleIncrement}
        disabled={page >= maxPage}
        marginLeft="12px"
      >
        Next
      </Button>
      <div className="inline-flex gap-3 items-center">
        <span className="px-4 py-1 inline-block">/</span>
        <span>Go to Page</span>
        <div className="inline-flex items-center gap-4">
          <Field width="32px">
            <NumberInputRoot
              width="65px"
              css={NumberInputStyle}
              value={pageValue}
              min={1}
              allowOverflow={false}
              onChange={handleNumberChange}
            >
              <NumberInputField />
            </NumberInputRoot>
          </Field>
          <Button
            variant="outline"
            textColor="#807F94"
            border="1px solid #E2E8F0"
            icon={<CaretRight />}
            isRightIcon
            type="button"
            onClick={handleSubmit}
            disabled={!allowPageJump}
          >
            Go
          </Button>
        </div>
      </div>
    </>
  );
};

export default PaginationMain;
